import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, UserRound } from "lucide-react";

export default function Agents() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [agents, setAgents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", hourly_rate: 50 });
  const [rates, setRates] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/agents");
      setAgents(r.data);
      setRates(Object.fromEntries(r.data.map((a) => [a.id, a.hourly_rate])));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/agents", { ...form, hourly_rate: parseFloat(form.hourly_rate) });
      toast.success("Agent created");
      setShowForm(false);
      setForm({ name: "", email: "", password: "", hourly_rate: 50 });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const saveRate = async (id) => {
    try {
      await api.patch(`/agents/${id}/rate`, { hourly_rate: parseFloat(rates[id]) });
      toast.success("Rate updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="agents-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">Agents and their hourly rates. Rates feed the billing report.</p>
        </div>
        {isAdmin && (
          <button data-testid="add-agent-button" onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 rounded-md bg-orange-500 hover:bg-orange-600 active:scale-[0.98] text-white text-sm font-semibold px-4 py-2.5 transition-all">
            <Plus className="h-4 w-4" /> New agent
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <form data-testid="create-agent-form" onSubmit={create} className="rounded-lg border border-orange-500/30 bg-card p-5 grid sm:grid-cols-4 gap-4">
          <input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="agent-name-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="agent-email-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="agent-password-input"
            className="rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
          <div className="flex gap-2">
            <input required type="number" min="0" step="0.5" placeholder="Rate R/h" value={form.hourly_rate}
              onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
              data-testid="agent-rate-input-new"
              className="w-full rounded-md bg-secondary/60 border border-border px-3 py-2 text-sm outline-none focus:border-orange-500/60" />
            <button type="submit" data-testid="agent-submit-button"
              className="rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-4 py-2 transition-colors">
              Add
            </button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {agents.map((a) => (
          <div key={a.id} data-testid={`agent-card-${a.id}`}
            className="rounded-lg border border-border bg-card p-5 hover:border-orange-500/40 transition-colors">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-secondary flex items-center justify-center">
                <UserRound className="h-5 w-5 text-orange-400" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{a.name}</p>
                <p className="text-xs text-muted-foreground truncate">{a.email}</p>
              </div>
              <span className={`ml-auto font-mono-digits text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${
                a.role === "admin" ? "bg-orange-500/15 text-orange-400" : "bg-secondary text-muted-foreground"
              }`}>{a.role}</span>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="font-mono-digits text-xs text-muted-foreground">R/h</span>
              <input
                type="number" min="0" step="0.5"
                data-testid={`agent-rate-input-${a.id}`}
                value={rates[a.id] ?? ""}
                disabled={!isAdmin}
                onChange={(e) => setRates({ ...rates, [a.id]: e.target.value })}
                className="w-24 rounded-md bg-secondary/60 border border-border px-2.5 py-1.5 text-sm font-mono-digits outline-none focus:border-orange-500/60 disabled:opacity-60"
              />
              {isAdmin && (
                <button data-testid={`agent-rate-save-${a.id}`} onClick={() => saveRate(a.id)}
                  className="rounded-md border border-orange-500/40 text-orange-400 hover:bg-orange-500/10 text-xs font-semibold px-3 py-1.5 transition-colors">
                  Save
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
